import { useState, type ReactNode } from "react";
import type { PublicCheckoutOffer } from "../../lib/checkoutCatalog";
import s from "./PricingPlans.module.css";
import { RollingNumber } from "./RollingNumber";

// Each bullet fits on one line at the narrowest card width (about 276px at
// 15px), so no feature wraps.
const individualPlans = [
  {
    key: "free",
    name: "Free",
    tag: "Meet TWT agents and our approach",
    includes: "Includes:",
    features: [
      "Limited access to TWT agents",
      "Earlier agent versions or delayed data",
      "Built to follow instructions all session",
      "The TWT research newsletter",
      "Previews of new agents and courses",
      "Access to the TWT Discord",
    ],
  },
  {
    key: "premium",
    name: "Pro",
    tag: "For active traders",
    includes: "Everything in Free, plus:",
    features: [
      "Every TWT agent, on real-time data",
      "The TWT Market Intelligence Platform",
      "Live news, options and quant analysis",
      "Web-based, with nothing to download",
      "Courses on markets and agentic AI",
      "Member-only Discord and alerts",
    ],
  },
  {
    key: "lifetime",
    name: "Max",
    tag: "For traders who want the most from TWT",
    includes: "Everything in Pro, plus:",
    features: [
      "Considerably more usage than Pro",
      "Beta access to new TWT agents",
      "All future updates included",
      "Priority support",
      "Max-only Discord channels and role",
    ],
  },
] as const;

export const organizationPlans = [
  {
    key: "team",
    name: "Team",
    tag: "For teams of 2–150",
    includes: "Everything in Pro, plus:",
    features: [
      "Central billing and administration",
      "Single sign-on (SSO)",
      "Shared projects and workspace search",
      "Mix and match seat types",
      "Priority support for your team",
    ],
  },
  {
    key: "enterprise",
    name: "Enterprise",
    tag: "Agentic AI access for organizations",
    includes: "Everything in Team, plus:",
    features: [
      "Single- or multi-tenant deployment",
      "Your own cloud or on-premises",
      "Forward deployed engineers",
      "Dedicated and on-site support",
      "Custom usage limits and seats",
      "Security review and custom terms",
    ],
  },
] as const;

type PlanKey =
  | (typeof individualPlans)[number]["key"]
  | (typeof organizationPlans)[number]["key"];

type Plan = {
  key: PlanKey;
  name: string;
  tag?: string;
  includes?: string;
  features: readonly string[];
};

type PlansView = "individual" | "team";

type Props = {
  offers: readonly PublicCheckoutOffer[];
  selectedOffer: PublicCheckoutOffer | null;
  onSelectOffer(offer: PublicCheckoutOffer): void;
  anchorAmount: string | null;
  /** Label on the longest cadence in the Pro card's pill, e.g. "Save 21%". */
  savings: string | null;
  /**
   * The selected cadence's saving in dollars, e.g. "Save $888 a year", shown
   * in the Pro kicker row when no badge is set; null when nothing is saved.
   */
  savingsLine?: string | null;
  premiumBadge?: ReactNode;
  /** Availability shown on the Pro card's corner ribbon. */
  premiumRibbon?: string | null;
  /**
   * The signed-in visitor's own plan. That card's CTA becomes a greyed,
   * disabled "Your current plan"; null (signed out) keeps every CTA live.
   */
  currentPlan?: "free" | "premium" | null;
  /** The pane the page resolved from the URL (?view=team). */
  initialView?: PlansView;
  actions: Record<PlanKey, ReactNode>;
};

export default function PricingPlans(props: Props) {
  const {
    offers,
    selectedOffer,
    onSelectOffer,
    anchorAmount,
    savings,
    savingsLine = null,
    premiumBadge,
    premiumRibbon = null,
    currentPlan = null,
    initialView = "individual",
    actions,
  } = props;

  const [view, setView] = useState<PlansView>(initialView);

  function selectView(next: PlansView) {
    setView(next);
    // Tab switches swap the pane in place: the URL gains ?view=team through
    // replaceState so a switch never creates a history entry.
    const url = new URL(window.location.href);
    if (next === "team") {
      url.searchParams.set("view", "team");
    } else {
      url.searchParams.delete("view");
    }
    window.history.replaceState(
      null,
      "",
      `${url.pathname}${url.search}${url.hash}`
    );
  }

  // The saving rides the longest cadence's segment, selected or not.
  const longestCycleMonths = offers.length
    ? Math.max(...offers.map((offer) => offer.monthsPerCycle))
    : 0;

  const cadencePill =
    offers.length > 0 ? (
      <div className={s.billPill} role="group" aria-label="Billing cadence">
        {offers.map((offer) => {
          const selected =
            selectedOffer?.key === offer.key &&
            selectedOffer.offerVersionMarker === offer.offerVersionMarker;
          return (
            <button
              key={offer.offerVersionMarker}
              type="button"
              aria-pressed={selected}
              data-selected={selected || undefined}
              onClick={() => onSelectOffer(offer)}
            >
              <span>{offer.label}</span>
              {offer.monthsPerCycle === longestCycleMonths &&
              longestCycleMonths > 1 &&
              savings ? (
                <span className={s.pillSave}>{savings}</span>
              ) : null}
            </button>
          );
        })}
      </div>
    ) : null;

  function cardFor(plan: Plan) {
    return (
      <article
        key={plan.key}
        className={s.card}
        aria-label={plan.name + " plan"}
        data-recommended={plan.key === "premium" || undefined}
        data-ribbon={(plan.key === "premium" && Boolean(premiumRibbon)) || undefined}
      >
        {plan.key === "premium" && premiumRibbon ? (
          <span className={s.cornerRibbon}>
            <span className="sr-only">{premiumRibbon}</span>
            <svg viewBox="0 0 176 176" aria-hidden="true">
              <polygon points="20,-8 20,0 28,0" className={s.ribbonFold} />
              <polygon points="176,148 184,156 176,156" className={s.ribbonFold} />
              <polygon points="20,-8 68,-8 184,108 184,156" className={s.ribbonFace} />
              <text x="110" y="58" transform="rotate(45 110 58)" textAnchor="middle" dominantBaseline="central">
                {premiumRibbon}
              </text>
            </svg>
          </span>
        ) : null}
        {/* Name and tagline share one row of the card grid, so a wrapped
            billing toggle under Pro's name moves the extra space above the
            price on its neighbours. */}
        <div className={s.cardHead}>
          <div className={s.kickerRow}>
            <div className={s.kicker}>{plan.name}</div>
            {plan.key === "premium" && cadencePill ? (
              <div className={s.billPillRow}>{cadencePill}</div>
            ) : null}
            {plan.key === "premium" && premiumBadge ? (
              <span
                className={
                  premiumBadge === "Recommended" ? s.badge : s.badgeMuted
                }
              >
                {premiumBadge}
              </span>
            ) : plan.key === "premium" && savingsLine ? (
              <span className={s.saveLine}>{savingsLine}</span>
            ) : null}
          </div>
          {plan.tag ? <p className={s.planTag}>{plan.tag}</p> : null}
        </div>

        <div className={s.priceBlock}>
          {plan.key === "free" ? (
            <>
              <div className={s.price}>
                <span className={s.amount}>$0</span>
              </div>
              <p className={s.billed}>Free forever. No card needed.</p>
            </>
          ) : plan.key === "premium" ? (
            <>
              <div className={s.price}>
                <span className={s.amount}>
                  <RollingAmount text={selectedOffer?.displayAmount ?? "—"} />
                </span>
                <span className={s.period}>
                  {selectedOffer?.displayPeriod ?? ""}
                </span>
                {anchorAmount ? (
                  <s className={s.anchor}>{anchorAmount}</s>
                ) : null}
              </div>
              <p className={s.billed}>{selectedOffer?.billingNote ?? ""}</p>
            </>
          ) : plan.key === "lifetime" ? (
            <>
              <div className={`${s.price} ${s.contact}`}>
                <span className={s.amountText}>Lifetime access</span>
              </div>
              {/* The card is named Max, so the price line is what tells the
                  buyer the tier is lifetime access. */}
              <p className={s.billed}>One payment. No subscription.</p>
            </>
          ) : plan.key === "team" ? (
            <>
              <div className={`${s.price} ${s.contact}`}>
                <span className={s.amountText}>Custom pricing</span>
              </div>
              <p className={s.billed}>Seat-based, sized to your team</p>
            </>
          ) : (
            <>
              <div className={`${s.price} ${s.contact}`}>
                <span className={s.amountText}>Custom pricing</span>
              </div>
              <p className={s.billed}>
                Contact our team to discuss enterprise pricing.
              </p>
            </>
          )}
        </div>

        {plan.includes ? <p className={s.includes}>{plan.includes}</p> : null}
        <ul className={s.bullets}>
          {plan.features.map((feature) => (
            <li key={feature}>{feature}</li>
          ))}
        </ul>

        <div className={s.cta}>
          {currentPlan === plan.key ? (
            <button
              type="button"
              className={`access-button ${s.currentPlan}`}
              disabled
            >
              Your current plan
            </button>
          ) : (
            actions[plan.key]
          )}
        </div>
      </article>
    );
  }

  return (
    <>
      <section className={s.hero}>
        <h1>Pricing</h1>
        <p>See pricing for our individual, team, and enterprise plans.</p>
      </section>

      <section className={s.plans} id="plans">
        <div className={s.head}>
          <div className={s.tabs} role="group" aria-label="Plan group">
            <button
              type="button"
              aria-pressed={view === "individual"}
              data-selected={view === "individual" || undefined}
              onClick={() => selectView("individual")}
            >
              <IndividualIcon />
              Individual
            </button>
            <button
              type="button"
              aria-pressed={view === "team"}
              data-selected={view === "team" || undefined}
              onClick={() => selectView("team")}
            >
              <TeamIcon />
              Team &amp; Enterprise
            </button>
          </div>
        </div>

        {view === "individual" ? (
          <div className={s.grid} aria-label="Individual plans">
            {individualPlans.map(cardFor)}
          </div>
        ) : (
          <div
            className={`${s.grid} ${s.gridOrganization}`}
            aria-label="Team and Enterprise plans"
          >
            {organizationPlans.map(cardFor)}
          </div>
        )}
      </section>
    </>
  );
}

// The two plan tabs carry a person and a group of people, as OpenAI's pricing
// page does (owner, Oct 2).
function IndividualIcon() {
  return (
    <svg
      aria-hidden="true"
      className={s.tabIcon}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.9}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="8" r="4" />
      <path d="M19 21v-1.5a4.5 4.5 0 0 0-4.5-4.5h-5A4.5 4.5 0 0 0 5 19.5V21" />
    </svg>
  );
}

// Material Symbols "groups", rounded and filled. Google, Apache License 2.0.
function TeamIcon() {
  return (
    <svg
      aria-hidden="true"
      className={[s.tabIcon, s.tabIconGroup].filter(Boolean).join(" ")}
      viewBox="0 -960 960 960"
      fill="currentColor"
    >
      <path d="M40-240q-17 0-28.5-11.5T0-280v-23q0-43 44-70t116-27q13 0 25 .5t23 2.5q-14 21-21 44t-7 48v65H40Zm240 0q-17 0-28.5-11.5T240-280v-25q0-32 17.5-58.5T307-410q32-20 76.5-30t96.5-10q53 0 97.5 10t76.5 30q32 20 49 46.5t17 58.5v25q0 17-11.5 28.5T680-240H280Zm500 0v-65q0-26-6.5-49T754-397q11-2 22.5-2.5t23.5-.5q72 0 116 26.5t44 70.5v23q0 17-11.5 28.5T920-240H780ZM160-440q-33 0-56.5-23.5T80-520q0-34 23.5-57t56.5-23q34 0 57 23t23 57q0 33-23 56.5T160-440Zm640 0q-33 0-56.5-23.5T720-520q0-34 23.5-57t56.5-23q34 0 57 23t23 57q0 33-23 56.5T800-440Zm-320-40q-50 0-85-35t-35-85q0-51 35-85.5t85-34.5q51 0 85.5 34.5T600-600q0 50-34.5 85T480-480Z" />
    </svg>
  );
}

const dollars = (value: number) => "$" + value.toLocaleString("en-US");

// The Pro price rolls to its new value when the billing period changes. A
// price that is not a plain dollar amount shows as written.
function RollingAmount({ text }: { text: string }) {
  const match = /^\$([\d,]+)$/.exec(text);
  if (!match) return <>{text}</>;
  return <RollingNumber value={Number(match[1]!.replace(/,/g, ""))} format={dollars} />;
}
